import { DateFormatMapping } from "@core/i18n/i18n";
import { getLanguageFromIntlLocale, intlLocaleToLocale } from "@core/i18n/locale-helper";
import { localeManager, serverConfigManager, legalContentManager } from "@core/service-container";
import { isPendingPatient, Patient, PendingPatient } from "@domain/patient/patient";

import { LandingPagesConfig } from "@domain/server-config/server-config";
import { isNotNil } from "@utils/assert";
import { isOldEnough } from "@utils/is-old-enough";
import { useObservable } from "micro-observables";
import { useCallback, useMemo } from "react";

interface UseCountryConfigHook {
	shouldDisplayLegalAgeDisclaimer: (patient: Patient | PendingPatient) => boolean;
	countryConfig: CountryConfig | null;
}

interface CountryConfig {
	customerSupportEmail: string;
	ptBiogenPrivacyNoticeUrl: string;
	patientBiogenPrivacyNoticeUrl: string;
	faqUrl: string;
	landingPages: LandingPagesConfig;
	dateFormats: DateFormatMapping;
	patientLegalAge: number;
	ptPhysiomePrivacyNoticeVersion: number;
	ptPhysiomeTermsOfUseVersion: number;
}

export function useCountryConfig(): UseCountryConfigHook {
	const locale = useObservable(localeManager.locale);
	const countryCode = useObservable(localeManager.countryCode);
	const serverConfigStatus = useObservable(serverConfigManager.serverConfigStatus);
	const legalDocumentsMetadata = useObservable(legalContentManager.legalDocumentsMetadata);

	const countryConfig: CountryConfig | null = useMemo(() => {
		if (!serverConfigStatus.available || !legalDocumentsMetadata) {
			return null;
		}

		const serverConfig = serverConfigManager.serverConfig;

		const _locale = intlLocaleToLocale(locale);
		const language = getLanguageFromIntlLocale(locale);

		return {
			customerSupportEmail: serverConfig.customerSupportEmails[countryCode],
			ptBiogenPrivacyNoticeUrl: legalDocumentsMetadata.ptBiogenPrivacyNoticeUrls[countryCode],
			patientBiogenPrivacyNoticeUrl: legalDocumentsMetadata.patientBiogenPrivacyNoticeUrls[countryCode],
			faqUrl: serverConfig.faqUrls[countryCode][language],
			landingPages: serverConfig.landingPages[countryCode][language],
			dateFormats: serverConfig.dateFormats[countryCode][_locale],
			patientLegalAge: serverConfig.patientLegalAges[countryCode],
			ptPhysiomePrivacyNoticeVersion: legalDocumentsMetadata.ptPhysiomePrivacyNoticeVersions[countryCode][_locale],
			ptPhysiomeTermsOfUseVersion: legalDocumentsMetadata.ptPhysiomeTermsOfUseVersions[countryCode][_locale],
		};
	}, [countryCode, locale, serverConfigStatus.available, legalDocumentsMetadata]);

	const shouldDisplayLegalAgeDisclaimer = useCallback(
		(patient: Patient | PendingPatient) =>
			!isPendingPatient(patient) &&
			isNotNil(patient.birthDate) &&
			isNotNil(countryConfig) &&
			!isOldEnough(patient.birthDate, countryConfig.patientLegalAge),
		[countryConfig],
	);

	return { shouldDisplayLegalAgeDisclaimer, countryConfig };
}
